import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  Box,
  Card,
  CardActionArea,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { PageHeader } from "@dmca/frontend/components/nav";
import { Link } from "@dmca/frontend/components/button";
import { useAppPermissions } from "@dmca/frontend/hooks";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderIcon from "@mui/icons-material/Folder";
import { navItems, type LinkItem } from "../../nav-items";
import { useHelpNavSection } from "../../use-help-nav-section";

export const Route = createFileRoute("/_authed/")({
  component: LandingPage,
  loader: async ({ context: { qc, trpcQcProxy } }) => {
    // hasPermission returns false for everything until tenant permissions
    // resolve, so without this the empty state flashes on first paint
    await Promise.all([
      qc.ensureQueryData(trpcQcProxy.auth.getTenantPermissions.queryOptions()),
      // Help cards are optional — don't fail Home if this errors
      qc.prefetchQuery(trpcQcProxy.help.getPages.queryOptions()),
    ]);
  },
  head: () => ({
    meta: [
      {
        title: "Home | DNA Dashboard",
      },
    ],
  }),
});

function LandingPage() {
  const hasPermission = useAppPermissions();
  const helpNavSection = useHelpNavSection();
  const theme = useTheme();
  // same chrome as the side nav drawer
  const brand = theme.palette.brand ?? theme.palette.primary;

  const sections = useMemo(() => {
    return [...navItems, helpNavSection]
      .filter(
        (section) => !section.permission || hasPermission(section.permission)
      )
      .map((section) => ({
        ...section,
        children: (section.children ?? []).filter(
          (item): item is LinkItem & { href: string } =>
            !!item.href && (!item.permission || hasPermission(item.permission))
        ),
      }))
      .filter((section) => section.children.length > 0);
  }, [hasPermission, helpNavSection]);

  return (
    <Container maxWidth="lg">
      <PageHeader>Home</PageHeader>

      {sections.length === 0 && (
        <Typography
          data-testid="landing-page-empty"
          sx={{ mt: 4 }}
          color="text.secondary"
        >
          You don't have access to any pages yet. Contact your administrator to
          request access.
        </Typography>
      )}

      <Stack gap={4} sx={{ mt: 2 }}>
        {sections.map((section) => (
          <Box key={section.label}>
            <Stack
              direction="row"
              gap={1}
              sx={{ alignItems: "center", mb: 1.5, color: "text.secondary" }}
            >
              {section.icon ? <section.icon /> : <FolderIcon />}
              <Typography variant="h6" component="h2">
                {section.label}
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              {section.children.map((item) => (
                <Grid key={item.href} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      backgroundColor: brand.main,
                      borderColor: brand.dark,
                    }}
                  >
                    <CardActionArea
                      data-testid="landing-page-card"
                      component={Link}
                      to={item.href}
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        p: 2,
                        textDecoration: "none",
                        color: brand.contrastText,
                        "&:hover": {
                          backgroundColor: brand.dark,
                        },
                        // the built-in white highlight washes out brand.dark
                        "&:hover .MuiCardActionArea-focusHighlight": {
                          opacity: 0,
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 500 }}>
                        {item.label}
                      </Typography>
                      <ChevronRightIcon />
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>
    </Container>
  );
}
